import { BaseViewStore } from 'core/stores';
import { observable, action, makeObservable } from 'mobx';
import { LookupStore } from 'modules/lookup/stores';
import { BulkPrintForm } from 'modules/order/forms';
import { lookupRouteName, sourceOfChange } from 'common/constants';
import { CreateBoxForm } from 'modules/product/forms';
import { FulfillmentOrderStore } from 'modules/order/stores';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import { createFileUrl } from 'core/utils';
import _ from 'lodash';

class BulkPrintViewStore extends BaseViewStore {
	@observable boxOptions = [];
	@observable tempBoxOptions = [];
	@observable pageBox = 1;
	@observable createBoxTemplateSuccessMessage = null;
	@observable createBoxTemplateErrorMessage = null;
	@observable isBoxDropdownLoading = false;
	@observable infiniteSearchPhrase = null;
	@observable selectedFulfillmentOrders = [];
	@observable debouncedBoxSearch = null;
	boxForm = new CreateBoxForm();
	bulkPrintForm = new BulkPrintForm({
		onSuccess: async () => {
			await this.printBulkList();
		},
	});

	constructor(rootStore, { parentViewStore }) {
		super(rootStore);
		makeObservable(this);
		this.lookupStore = new LookupStore(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.shipmentInfoStore = new ShipmentInfoStore(rootStore);
		this.parentView = parentViewStore;
		this.bulkPrintForm.state.options.set({
			validateOnChange: true,
		});
		this.boxForm.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.debouncedBoxSearch = _.debounce((searchPhrase, { action }) => {
			this.searchBoxAsync(searchPhrase, { action });
		}, 500);
		await Promise.all([this.getBoxOptions(), this.getFulfillmentOrders()]);
		if (
			// If none of the selected Fulfillment Orders has box required, then bulk print immediately, skip choose box step.
			!_.some(this.selectedFulfillmentOrders, fulfillmentOrder => {
				return fulfillmentOrder.isBoxRequired;
			})
		) {
			await this.printBulkList();
		}
	};

	@action.bound
	printBulkList = async () => {
		try {
			this.suspend();
			let bulkPrintFulfillmentOrders = [];
			_.forEach(this.selectedFulfillmentOrders, fulfillmentOrder => {
				const bulkPrintFulfillmentOrder = {
					fulfillmentOrderId: fulfillmentOrder.id,
					boxId: fulfillmentOrder.box ? fulfillmentOrder.box.id : null, // fulfillmentOrder.box is set in onChange() function.
				};
				bulkPrintFulfillmentOrders.push(bulkPrintFulfillmentOrder);
			});
			const bulkPrintHistoryItems = _.map(this.selectedFulfillmentOrders, fulfillmentOrder => {
				return { fulfillmentOrderId: fulfillmentOrder.id };
			});
			const bulkPrintHistory = {
				numberOfFulfillmentOrders: bulkPrintHistoryItems.length,
				bulkPrintHistoryItems: bulkPrintHistoryItems,
			};

			await this.shipmentInfoStore.bulkPrint(bulkPrintFulfillmentOrders, sourceOfChange.shipmentBulkPrintBulkList);
			this.resume();
			let openPrintWindow = false;
			if (this.shipmentInfoStore.item.data.failedCount > 0 && this.shipmentInfoStore.item.data.labels != '') {
				this.rootStore.notificationStore.error('FULFILLMENTORDER.BULKPRINT.PARTIALYPRINTED');
				openPrintWindow = true;
			}
			if (this.shipmentInfoStore.item.data.labels === '') {
				this.rootStore.notificationStore.error('FULFILLMENTORDER.BULKPRINT.EMPTY');
				openPrintWindow = false;
			} else {
				this.parentView.pdfURL = createFileUrl(this.shipmentInfoStore.item.data.labels);
				openPrintWindow = true;
			}
			this.parentView.selectedItems = [];
			this.parentView.selectedGroups = [];
			this.parentView.printPdf = true;
			this.parentView.findFulfillmentOrders(openPrintWindow);
			this.parentView.pickedFulfillmentOrdersForBulkPrintHistory = bulkPrintHistory;
			this.parentView.openBulkPrintHistoryCreateModal();
		} catch (error) {
			this.rootStore.notificationStore.error('FULFILLMENTORDER.BULKPRINT.ERROR');
		}
		this.parentView.bulkPrintModal.close();
	};

	@action.bound
	getFulfillmentOrders = async () => {
		this.suspend();
		const fulfillmentOrderIds = _.map(this.parentView.selectedItems, item => {
			return item.id;
		});
		this.selectedFulfillmentOrders = await this.fulfillmentOrderStore.getFulfillmentOrders({
			ids: fulfillmentOrderIds,
			embed: ['fulfillmentOrderItems', 'fulfillmentOrderItems.product'],
		});

		await this.prepareData();
		this.resume();
	};

	@action.bound
	prepareData = async () => {
		_.each(this.selectedFulfillmentOrders, fulfillmentOrder => {
			const isBoxRequired =
				fulfillmentOrder.fulfillmentOrderItems.length > 1 ||
				_.some(fulfillmentOrder.fulfillmentOrderItems, item => {
					return item.quantity > 1;
				});

			fulfillmentOrder.isBoxRequired = isBoxRequired;
		});
		this.selectedFulfillmentOrders = _.orderBy(
			this.selectedFulfillmentOrders,
			['isBoxRequired', 'fulfillmentOrderId'],
			['desc', 'desc']
		);
		_.each(this.selectedFulfillmentOrders, fulfillmentOrder => {
			this.bulkPrintForm.$('fulfillmentOrders').add([
				{
					id: fulfillmentOrder.id,
					fulfillmentOrderId: fulfillmentOrder.fulfillmentOrderId,
					isBoxRequired: fulfillmentOrder.isBoxRequired,
					box: null,
					fulfillmentOrderItems: fulfillmentOrder.fulfillmentOrderItems,
				},
			]);
		});
	};

	@action.bound
	onChange = (field, value, fulfillmentOrderId) => {
		field.set(value);
		_.each(this.selectedFulfillmentOrders, fulfillmentOrder => {
			if (fulfillmentOrder.id === fulfillmentOrderId) {
				fulfillmentOrder.box = value.extra;
			}
		});
	};

	@action.bound
	getBoxOptions = async () => {
		await this.lookupStore.find({ page: 1, rpp: 15 }, lookupRouteName.box);
		this.boxOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
	};

	@action.bound
	async onMenuClose() {
		this.pageBox = 1;
		this.infiniteSearchPhrase = null;
		await this.getBoxOptions();
	}

	@action.bound
	handleOnBoxMenuScroll = async () => {
		if (this.boxOptions.length < this.pageBox * 15) return;
		this.isBoxDropdownLoading = true;
		this.pageBox++;
		await this.lookupStore.find(
			{ search: this.infiniteSearchPhrase, page: this.pageBox, rpp: 15 },
			lookupRouteName['box']
		);
		this.tempBoxOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
		if (this.tempBoxOptions.length > 0) {
			this.boxOptions = this.boxOptions.concat(this.tempBoxOptions);
		}
		this.isBoxDropdownLoading = false;
	};

	@action.bound
	async searchBoxAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.page = 1;
		}
		if (action === 'input-change') {
			this.isBoxDropdownLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageBox = 1;
			this.boxOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
			};
			await this.lookupStore.find(filter, lookupRouteName['box']);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.boxOptions = newOptions;
			this.isBoxDropdownLoading = false;
		}
	}

	@action.bound
	onCreateBoxTemplate = async () => {
		this.boxForm.validate().then(async ({ isValid }) => {
			try {
				if (isValid) {
					const { name, boxWidth, boxHeight, boxLength } = this.boxForm.values();

					const boxValues = {
						name,
						boxWidth,
						boxHeight,
						boxLength,
					};
					const response = await this.lookupStore.createLookup(
						boxValues,
						lookupRouteName.box,
						sourceOfChange.createLookup
					);
					if (response.message) {
						this.boxForm.$('name').invalidate(response.message);
					} else {
						var createdBox = this.lookupStore.item;
						if (createdBox) {
							this.createBoxTemplateSuccessMessage = 'FULFILLMENTORDER.BULKPRINT.CREATEBOX.SUCCESS';
							this.boxForm.$('name').clear();
							this.boxForm.$('boxHeight').clear();
							this.boxForm.$('boxWidth').clear();
							this.boxForm.$('boxLength').clear();
							this.boxOptions.push({ value: createdBox.id, label: createdBox.name, extra: createdBox });
						}
					}
				} else {
					this.boxForm.showErrors(true);
				}
			} catch (error) {
				this.createBoxTemplateErrorMessage = 'FULFILLMENTORDER.BULKPRINT.CREATEBOX.ERROR';
			} finally {
				setTimeout(() => {
					this.createBoxTemplateSuccessMessage = null;
					this.createBoxTemplateErrorMessage = null;
				}, 2500);
			}
		});
	};
}

export default BulkPrintViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/BulkPrintViewStore.js